import {Component, DestroyRef, inject, OnInit, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute} from '@angular/router';
import {
    MsisdnApiService,
    MsisdnContactDetailsResponse,
    MsisdnIncidentResponse,
    MsisdnOwnerResponse,
    MsisdnResponse,
    ReferenceBookResponse,
    ReferenceBookType,
} from '@npck/af-frontend-api';
import {
    DATE_UI_FORMAT,
    formatDateTimeFromServerToUi,
    LocalizationService,
    normalizePhone,
    RouterService,
    rxSpinner,
    TAB,
} from '@npck/libs/commons';
import {DialogService, SnackbarService} from '@npck/libs/ui-kit';
import dayjs from 'dayjs';
import {EMPTY, forkJoin, mergeMap} from 'rxjs';
import {applyLargeEllipsis, Column, ColumnType, Row, StatusColumn} from '../../../components/full-table/table/table.component';
import {mapIncidentStatusToStyle, mapMsisdnStatusToStyle} from '../../../components/status/status.component';
import {AuthHolderService} from '../../../domain/auth-holder.service';
import {DictionaryHolderService, findValueInDictionary, resolveRefName} from '../../../domain/dictionary-holder.service';
import {FieldInfo} from '../../../domain/field-builder.service';
import {MSISDN_ID} from '../../../domain/query-params.keys';
import {MainContainerComponent} from '../../../layout/main-container/main-container.component';
import {Path} from '../../../path';
import {OTHER_VALUE} from '../../incidents/create-edit/contact-details-step/contact-details-step.component';

type MsisdnDetailsTab = 'owners' | 'incidents' | 'contacts';

@Component({
    selector: 'af-msisdn-details',
    templateUrl: 'msisdn-details.component.html',
    styleUrls: ['msisdn-details.component.scss'],
    standalone: false,
})
export class MsisdnDetailsComponent implements OnInit {
    @ViewChild('mainContainerComponent') mainContainerComponent!: MainContainerComponent;
    msisdnId!: string;
    msisdn: MsisdnResponse | undefined;
    msisdnFields: FieldInfo[] = [];
    authorFields: FieldInfo[] = [];
    tabs: MsisdnDetailsTab[] = ['owners', 'incidents', 'contacts'];
    selectedTabIndex = 0;
    activeTab: MsisdnDetailsTab = 'owners';
    dataSource: Row[] = [];
    canDelete: boolean;

    private destroyRef = inject(DestroyRef);
    private dictionaries: Partial<Record<ReferenceBookType, ReferenceBookResponse[]>> = {};
    private detailsDictionaries: Record<string, ReferenceBookResponse[]> = {};

    constructor(
        private activatedRoute: ActivatedRoute,
        private routerService: RouterService,
        private msisdnApiService: MsisdnApiService,
        private localizationService: LocalizationService,
        private dialogService: DialogService,
        private snackbarService: SnackbarService,
        private dictionaryHolderService: DictionaryHolderService,
        private authHolderService: AuthHolderService,
    ) {
        this.msisdnId = this.activatedRoute.snapshot.paramMap.get(MSISDN_ID)!;
        this.canDelete = this.authHolderService.hasAccess(['npck-admin']);
    }

    ngOnInit(): void {
        forkJoin({
            response: this.msisdnApiService.getMsisdn(this.msisdnId),
            [ReferenceBookType.BIN]: this.dictionaryHolderService.getReference(ReferenceBookType.BIN),
            [ReferenceBookType.INCIDENT_STATUS]: this.dictionaryHolderService.getReference(ReferenceBookType.INCIDENT_STATUS),
            [ReferenceBookType.CONTACT_TYPE]: this.dictionaryHolderService.getReference(ReferenceBookType.CONTACT_TYPE),
            [ReferenceBookType.CONTACT_TIMEFRAME]: this.dictionaryHolderService.getReference(ReferenceBookType.CONTACT_TIMEFRAME),
            [ReferenceBookType.FRAUDSTER_ROLE]: this.dictionaryHolderService.getReference(ReferenceBookType.FRAUDSTER_ROLE),
            [ReferenceBookType.CONTACT_REASON]: this.dictionaryHolderService.getReference(ReferenceBookType.CONTACT_REASON),
            [ReferenceBookType.VICTIM_ACTION]: this.dictionaryHolderService.getReference(ReferenceBookType.VICTIM_ACTION),
            [ReferenceBookType.MESSENGER_CONTACT_TYPE]: this.dictionaryHolderService.getReference(ReferenceBookType.MESSENGER_CONTACT_TYPE),
            [ReferenceBookType.CALL_CONTACT_TYPE]: this.dictionaryHolderService.getReference(ReferenceBookType.CALL_CONTACT_TYPE),
            [ReferenceBookType.MESSENGER_CONTACT_SUBTYPE]:
                this.dictionaryHolderService.getReference(ReferenceBookType.MESSENGER_CONTACT_SUBTYPE),
            [ReferenceBookType.EMAIL_CONTACT_TYPE]: this.dictionaryHolderService.getReference(ReferenceBookType.EMAIL_CONTACT_TYPE),
            [ReferenceBookType.SOCIAL_CONTACT_TYPE]: this.dictionaryHolderService.getReference(ReferenceBookType.SOCIAL_CONTACT_TYPE),
            [ReferenceBookType.INTERNET_CONTACT_TYPE]: this.dictionaryHolderService.getReference(ReferenceBookType.INTERNET_CONTACT_TYPE),
            [ReferenceBookType.MSISDN_STATUS]: this.dictionaryHolderService.getReference(ReferenceBookType.MSISDN_STATUS),
            [ReferenceBookType.MOBILE_OPERATOR_EVENT_SOURCE]:
                this.dictionaryHolderService.getReference(ReferenceBookType.MOBILE_OPERATOR_EVENT_SOURCE),
            [ReferenceBookType.COUNTRY]: this.dictionaryHolderService.getReference(ReferenceBookType.COUNTRY),
        })
            .pipe(rxSpinner('initial'), takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({response, ...referenceBooks}) => {
                    this.msisdn = response;
                    this.dictionaries = referenceBooks;
                    this.detailsDictionaries = {
                        CALL: referenceBooks[ReferenceBookType.CALL_CONTACT_TYPE] ?? [],
                        MESSENGER: referenceBooks[ReferenceBookType.MESSENGER_CONTACT_SUBTYPE] ?? [],
                        EMAIL: referenceBooks[ReferenceBookType.EMAIL_CONTACT_TYPE] ?? [],
                        SOCIAL: referenceBooks[ReferenceBookType.SOCIAL_CONTACT_TYPE] ?? [],
                        INTERNET: referenceBooks[ReferenceBookType.INTERNET_CONTACT_TYPE] ?? [],
                    };
                    this.buildInfoFields();
                    this.buildDataSourceForTab(this.activeTab);
                },
                error: () => {
                    this.routerService.navigate(Path.SUBJECTS, {queryParams: {[TAB]: 'tabSuspiciousNumbers'}});
                },
            });
    }

    breadcrumbCustomizer = (path: string): string => {
        if (this.msisdnId === path) {
            return this.localizationService.getLocalizedKey('i18n_af_msisdnDetailsTitle', {msisdnId: this.msisdnId});
        }
        return path;
    };


    handleNavigation($event: number): void {
        this.activeTab = this.tabs[$event];
        this.buildDataSourceForTab(this.activeTab);
    }

    openDeleteDialog(): void {
        this.dialogService.openDialog({
            title: 'i18n_af_msisdnDeletionDialogTitle',
            subtitle: 'i18n_af_msisdnDeletionDialogSubtitle',
            buttonOkText: 'i18n_af_buttonDelete',
            buttonCancelText: 'i18n_af_buttonCancel',
            isButtonOkAccent: true,
        }).pipe(
            mergeMap(dialogResult => !dialogResult ? EMPTY : this.msisdnApiService.deleteMsisdn(this.msisdnId)
                .pipe(rxSpinner('deleting'))),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(() => {
            this.snackbarService.showSnackBar('i18n_af_msisdnDeletedSnackbar');
            this.mainContainerComponent.breadcrumbs.navigateBack();
        });
    }

    private buildInfoFields(): void {
        const m = this.msisdn!;
        const orgName = this.resolveBinName(m.createdByOrganization);

        this.msisdnFields = this.filterEmpty([
            {fieldName: 'msisdn', fieldValue: normalizePhone(m.msisdn)},
            {
                fieldName: 'status', fieldValue: m.isDeleted
                    ? this.localizationService.getLocalizedKey('i18n_af_filter_field_status#deleted')
                    : this.localizationService.getLocalizedKey('i18n_af_filter_field_status#active'),
            },
        ]);

        const creationMethodDisplay = m.creationMethod
            ? this.localizationService.getLocalizedKey(`i18n_af_filter_field_creationMethod#${m.creationMethod}`)
            : '—';
        this.authorFields = this.filterEmpty([
            {fieldName: 'createdBy', fieldValue: m.createdBy},
            {fieldName: 'createdByOrganization', fieldValue: orgName},
            {fieldName: 'creationMethod', fieldValue: creationMethodDisplay},
            {fieldName: 'createdDate', fieldValue: this.formatDateTime(m.createdDate)},
            {fieldName: 'updatedDate', fieldValue: this.formatDateTime(m.updatedDate)},
            {fieldName: 'updatedBy', fieldValue: m.updatedBy},
        ]);
    }

    private buildDataSourceForTab(tab: MsisdnDetailsTab): void {
        this.dataSource = [];
        if (!this.msisdn) {
            return;
        }
        switch (tab) {
            case 'owners':
                this.dataSource = (this.msisdn.owners || []).map(owner => this.buildOwnerRow(owner));
                break;
            case 'incidents':
                this.dataSource = (this.msisdn.incidents || []).map(incident => this.buildIncidentRow(incident));
                break;
            case 'contacts':
                this.dataSource = (this.msisdn.contactDetails || []).map(cd => this.buildContactRow(cd));
                break;
        }
    }

    private buildIncidentRow(incident: MsisdnIncidentResponse): Row {
        const relatedOrgs = (incident.relatedOrganizations || [])
            .map(bin => this.resolveBinName(bin)).join(', ');
        const govOrg = incident.governmentOrganization
            ? this.resolveBinName(incident.governmentOrganization)
            : '';
        const authorOrg = this.resolveBinName(incident.createdByOrganization);
        const incidentStatusDisplay = resolveRefName(this.getDictionary(ReferenceBookType.INCIDENT_STATUS), incident.incidentStatus);
        const resolutionStatusDisplay = incident.resolutionStatus
            ? resolveRefName(this.getDictionary(ReferenceBookType.INCIDENT_STATUS), incident.resolutionStatus)
            : '';

        return <Row>{
            incidentId: <Column>{
                type: ColumnType.LINK,
                text: incident.id,
                click: () => this.navigateToIncident(incident.id),
            },
            relatedOrganizations: <Column>{
                type: ColumnType.TEXT,
                text: relatedOrgs,
                ...applyLargeEllipsis(relatedOrgs),
            },
            governmentOrganization: <Column>{
                type: ColumnType.TEXT,
                text: govOrg,
            },
            authorOrganization: <Column>{
                type: ColumnType.TEXT,
                text: authorOrg,
            },
            incidentStatus: <StatusColumn>{
                type: ColumnType.STATUS,
                text: incidentStatusDisplay,
                style: mapIncidentStatusToStyle(incidentStatusDisplay),
            },
            resolutionStatus: <Column>{
                type: ColumnType.BUTTON,
                text: resolutionStatusDisplay,
                iconPosition: 'right',
                icon: 'ic_popup',
                click: () => this.navigateToIncident(incident.id),
            },
            createdDate: <Column>{
                type: ColumnType.DATE_TIME_SECONDS,
                text: incident.createdDate,
            },
        };
    }

    private buildOwnerRow(owner: MsisdnOwnerResponse): Row {
        const ownershipPeriod = this.formatOwnershipPeriod(owner.checkFromDate, owner.checkToDate);
        const msisdnStatusDisplay = owner.msisdnStatus
            ? resolveRefName(this.getDictionary(ReferenceBookType.MSISDN_STATUS), owner.msisdnStatus)
            : '';
        const isResidentDisplay = owner.isResident != null
            ? this.localizationService.getLocalizedKey(
                owner.isResident ? 'i18n_af_residentStatus#resident' : 'i18n_af_residentStatus#nonResident',
            )
            : '';
        const registeredMsisdnsDisplay = (owner.registeredMsisdns || [])
            .map(m => normalizePhone(m)).join(', ');
        const detectedByDisplay = owner.detectedBy
            ? resolveRefName(this.getDictionary(ReferenceBookType.MOBILE_OPERATOR_EVENT_SOURCE), owner.detectedBy)
            : '';

        return <Row>{
            incidentId: <Column>{
                type: ColumnType.LINK,
                text: owner.incidentId,
                click: () => this.navigateToIncident(owner.incidentId),
            },
            fullName: <Column>{
                type: ColumnType.TEXT,
                text: owner.fullName,
                ...applyLargeEllipsis(owner.fullName),
            },
            isResident: <Column>{
                type: ColumnType.TEXT,
                text: isResidentDisplay,
            },
            iin: <Column>{
                type: ColumnType.TEXT,
                text: owner.iin,
            },
            passportNumber: <Column>{
                type: ColumnType.TEXT,
                text: owner.passportNumber,
            },
            residencyCountry: <Column>{
                type: ColumnType.TEXT,
                text: this.resolveCountryName(owner.residencyCountry),
            },
            ownershipPeriod: <Column>{
                type: ColumnType.TEXT,
                text: ownershipPeriod,
            },
            msisdnStatus: <StatusColumn>{
                type: ColumnType.STATUS,
                text: msisdnStatusDisplay,
                style: mapMsisdnStatusToStyle(owner.msisdnStatus),
            },
            occurredDate: <Column>{
                type: ColumnType.DATE_TIME_SECONDS,
                text: owner.occurredDate,
            },
            registeredMsisdns: <Column>{
                type: ColumnType.TEXT,
                text: registeredMsisdnsDisplay,
                ...applyLargeEllipsis(registeredMsisdnsDisplay),
            },
            detectedBy: <Column>{
                type: ColumnType.TEXT,
                text: detectedByDisplay,
                ...applyLargeEllipsis(detectedByDisplay),
            },
            activationDate: <Column>{
                type: ColumnType.DATE_TIME_SECONDS,
                text: owner.activationDate,
            },
            terminationDate: <Column>{
                type: ColumnType.DATE_TIME_SECONDS,
                text: owner.terminationDate,
            },
        };
    }

    private buildContactRow(cd: MsisdnContactDetailsResponse): Row {
        const contactTypeDisplay = resolveRefName(this.getDictionary(ReferenceBookType.CONTACT_TYPE), cd.contactType);
        const detailsDisplay = this.resolveContactDetails(cd);
        const messengerDisplay = this.resolveEnumOrOther(
            this.getDictionary(ReferenceBookType.MESSENGER_CONTACT_TYPE), cd.messengerContactType, cd.messengerContactTypeOther,
        );
        const msisdnDisplay = this.msisdn ? normalizePhone(this.msisdn.msisdn) : '';
        const contactTimeframeDisplay = resolveRefName(this.getDictionary(ReferenceBookType.CONTACT_TIMEFRAME), cd.contactTimeframe);
        const fraudsterRoleDisplay = this.resolveEnumOrOther(
            this.getDictionary(ReferenceBookType.FRAUDSTER_ROLE), cd.fraudsterRole, cd.fraudsterRoleOther,
        );
        const contactReasonDisplay = this.resolveEnumOrOther(
            this.getDictionary(ReferenceBookType.CONTACT_REASON), cd.contactReason, cd.contactReasonOther,
        );
        const victimActionsDisplay = (cd.victimActions || [])
            .map(va => va === OTHER_VALUE ? cd.victimActionOther : resolveRefName(this.getDictionary(ReferenceBookType.VICTIM_ACTION), va))
            .filter(Boolean).join(', ');

        return <Row>{
            contactType: <Column>{type: ColumnType.TEXT, text: contactTypeDisplay},
            details: <Column>{
                type: ColumnType.TEXT,
                text: detailsDisplay,
                ...applyLargeEllipsis(detailsDisplay),
            },
            messengerContactType: <Column>{type: ColumnType.TEXT, text: messengerDisplay},
            msisdn: <Column>{type: ColumnType.TEXT, text: msisdnDisplay},
            contactDate: <Column>{type: ColumnType.DATE_TIME_SECONDS, text: cd.contactDate},
            contactTimeframe: <Column>{type: ColumnType.TEXT, text: contactTimeframeDisplay},
            fraudsterRole: <Column>{
                type: ColumnType.TEXT,
                text: fraudsterRoleDisplay,
                ...applyLargeEllipsis(fraudsterRoleDisplay),
            },
            contactReason: <Column>{
                type: ColumnType.TEXT,
                text: contactReasonDisplay,
                ...applyLargeEllipsis(contactReasonDisplay),
            },
            victimActions: <Column>{
                type: ColumnType.TEXT,
                text: victimActionsDisplay,
                ...applyLargeEllipsis(victimActionsDisplay),
            },
        };
    }

    private navigateToIncident(incidentId: string): void {
        this.routerService.navigate(`${Path.INCIDENTS}/${incidentId}`);
    }

    private resolveEnumOrOther(dictionary: ReferenceBookResponse[], value: string, otherValue: string): string {
        if (!value) {
            return '';
        }
        return value === OTHER_VALUE ? otherValue ?? '' : resolveRefName(dictionary, value);
    }

    private getDictionary(type: ReferenceBookType): ReferenceBookResponse[] {
        return this.dictionaries[type] ?? [];
    }

    private resolveBinName(bin: string): string {
        if (!bin) {
            return '';
        }
        return findValueInDictionary(this.getDictionary(ReferenceBookType.BIN), bin, 'name_ru') || bin;
    }

    private resolveCountryName(code: string): string {
        if (!code) {
            return '';
        }
        return findValueInDictionary(this.getDictionary(ReferenceBookType.COUNTRY), code, 'name_ru') || code;
    }

    private resolveContactDetails(cd: MsisdnContactDetailsResponse): string {
        const resolverMap: Record<string, {value: string; otherValue: string; dictionary: ReferenceBookResponse[]}> = {
            CALL: {
                value: cd.callContactType, otherValue: cd.callContactTypeOther,
                dictionary: this.detailsDictionaries.CALL ?? [],
            },
            MESSENGER: {
                value: cd.messengerContactSubtype, otherValue: cd.messengerContactSubtypeOther,
                dictionary: this.detailsDictionaries.MESSENGER ?? [],
            },
            EMAIL: {
                value: cd.emailContactType, otherValue: cd.emailContactTypeOther,
                dictionary: this.detailsDictionaries.EMAIL ?? [],
            },
            SOCIAL: {
                value: cd.socialContactType, otherValue: cd.socialContactTypeOther,
                dictionary: this.detailsDictionaries.SOCIAL ?? [],
            },
            INTERNET: {
                value: cd.internetContactType, otherValue: cd.internetContactTypeOther,
                dictionary: this.detailsDictionaries.INTERNET ?? [],
            },
        };
        const resolver = resolverMap[cd.contactType];
        if (!resolver?.value) {
            return cd.details ?? '';
        }
        return resolver.value === OTHER_VALUE
            ? resolver.otherValue ?? ''
            : resolveRefName(resolver.dictionary, resolver.value);
    }

    private formatDateTime(value: string): string {
        if (!value) {
            return '';
        }
        return formatDateTimeFromServerToUi(dayjs(value));
    }

    private formatOwnershipPeriod(fromDate: string, toDate: string): string {
        const from = fromDate ? dayjs(fromDate).format(DATE_UI_FORMAT) : '';
        const to = toDate ? dayjs(toDate).format(DATE_UI_FORMAT) : '';
        if (from && to) {
            return `${from} - ${to}`;
        }
        return from || to || '';
    }

    private filterEmpty(fields: FieldInfo[]): FieldInfo[] {
        return fields.filter(f => !!f.fieldValue);
    }
}
